// Componentes de filas para BidsRankingView
// Incluye: BidderRow, NonBidderRow, RankBadge, ConditionsTooltip

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Trophy, Check, Info, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { BidJourneyTimeline } from './BidJourneyTimeline';
import { BuyerDetailModal } from '../BuyerDetailModal';
import { maskEmail } from '../../../../utils/maskSensitiveData';

// ============================================
// UTILIDADES
// ============================================

/**
 * Formatea un monto en euros
 */
export const formatAmount = (amount) => {
  if (!amount) return '—';
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Formatea fecha/hora de puja
 */
export const formatBidTime = (timestamp) => {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ============================================
// COMPONENTES AUXILIARES
// ============================================

/**
 * Badge de ranking (medallas para top 3)
 */
export const RankBadge = ({ rank }) => {
  if (rank === 1) {
    return (
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-500/20 text-amber-400">
        <Trophy className="w-5 h-5" />
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-400/20 text-slate-300 font-bold text-sm">
        2º
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-700/20 text-orange-400 font-bold text-sm">
        3º
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-700/50 text-slate-400 font-medium text-sm">
      {rank}º
    </div>
  );
};

/**
 * Componente de tooltip para condiciones
 */
export const ConditionsTooltip = ({ conditions, conditionsText }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const hasConditions = (conditions && conditions.length > 0) || conditionsText;

  if (!hasConditions) {
    return <span className="text-slate-500">—</span>;
  }

  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="p-1 rounded hover:bg-slate-700 transition-colors"
      >
        <Info className="w-4 h-4 text-amber-400" />
      </button>

      {showTooltip && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-xl">
          <div className="text-xs text-slate-300 space-y-2">
            {conditions && conditions.length > 0 && (
              <div>
                <p className="font-semibold text-slate-200 mb-1">Condiciones:</p>
                <ul className="list-disc list-inside space-y-1">
                  {conditions.map((cond, i) => (
                    <li key={i}>{cond}</li>
                  ))}
                </ul>
              </div>
            )}
            {conditionsText && (
              <div>
                <p className="font-semibold text-slate-200 mb-1">Notas:</p>
                <p className="text-slate-400 italic">"{conditionsText}"</p>
              </div>
            )}
          </div>
          {/* Flecha del tooltip */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-slate-800"></div>
        </div>
      )}
    </div>
  );
};

// ============================================
// FILAS DE LA TABLA
// ============================================

/**
 * Fila de usuario con historial de pujas (expandible)
 */
export const BidderRow = ({
  bidder,
  rank,
  topBid,
  isWinner,
  journey,
  propertyId,
  buyerSummary,
  isExpanded,
  onToggleExpand,
  isSpectatorMode = false
}) => {
  const [showDetailModal, setShowDetailModal] = useState(false);
  const { email, name, bids, maxBid, maxBidIsLastChance, maxBidTime, maxBidConditions, maxBidConditionsText } = bidder;

  // Enmascarar email si es modo espectador
  const displayEmail = isSpectatorMode ? maskEmail(email) : email;
  
  // Calcular diferencia con la puja ganadora
  const difference = topBid - maxBid;
  const differencePercent = topBid > 0 ? ((difference / topBid) * 100).toFixed(1) : 0;

  // Contar intentos (journey sin éxito)
  const attemptCount = journey ? journey.filter(j => j.status !== 'success').length : 0;

  return (
    <>
      <tr 
        className={`border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors cursor-pointer ${isWinner ? 'bg-green-900/10' : ''}`}
        onClick={onToggleExpand}
      >
        {/* Expandir */}
        <td className="py-4 px-2 w-8">
          <button className="p-1 hover:bg-slate-700 rounded transition-colors">
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </button>
        </td>

        {/* Ranking */}
        <td className="py-4 px-3">
          <RankBadge rank={rank} />
        </td>

        {/* Usuario */}
        <td className="py-4 px-3">
          <div>
            <p className="font-medium text-white">{name || 'Sin nombre'}</p>
            <p className="text-xs text-slate-400">{displayEmail}</p>
          </div>
        </td>

        {/* Puja máxima */}
        <td className="py-4 px-3">
          <span className={`font-bold text-lg ${isWinner ? 'text-green-400' : 'text-white'}`}>
            {formatAmount(maxBid)}
          </span>
        </td>

        {/* Intentos */}
        <td className="py-4 px-3 text-center">
          {attemptCount > 0 ? (
            <span className="bg-orange-500/20 text-orange-400 text-xs px-2 py-1 rounded-full">
              {attemptCount}
            </span>
          ) : (
            <span className="text-slate-600">—</span>
          )}
        </td>

        {/* Última oportunidad */}
        <td className="py-4 px-3 text-center">
          {maxBidIsLastChance ? (
            <Check className="w-5 h-5 text-amber-400 mx-auto" />
          ) : (
            <span className="text-slate-600">—</span>
          )}
        </td>

        {/* Fecha/hora de la puja máxima */}
        <td className="py-4 px-3">
          <span className="text-sm text-slate-400">
            {formatBidTime(maxBidTime)}
          </span>
        </td>

        {/* Condiciones */}
        <td className="py-4 px-3 text-center" onClick={e => e.stopPropagation()}>
          <ConditionsTooltip 
            conditions={maxBidConditions} 
            conditionsText={maxBidConditionsText} 
          />
        </td>

        {/* Diferencia con ganador */}
        <td className="py-4 px-3">
          {isWinner ? (
            <span className="text-green-400 font-semibold flex items-center gap-1">
              <Trophy className="w-4 h-4" />
              Líder
            </span>
          ) : (
            <span className="text-red-400 text-sm">
              -{formatAmount(difference)}
              <span className="text-slate-500 ml-1">({differencePercent}%)</span>
            </span>
          )}
        </td>

        {/* # Pujas */}
        <td className="py-4 px-3 text-center">
          <span className="bg-slate-700 text-slate-300 text-xs px-2 py-1 rounded-full">
            {bids.length}
          </span>
        </td>

        {/* Ver detalle */}
        <td className="py-4 px-3" onClick={e => e.stopPropagation()}>
          {buyerSummary && (
            <button
              onClick={() => setShowDetailModal(true)}
              className="text-amber-400 hover:text-amber-300 transition-colors"
              title="Ver detalle completo"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
        </td>
      </tr>

      {/* Fila expandida con journey */}
      {isExpanded && (
        <tr className="bg-slate-900/50">
          <td colSpan={11} className="px-4 py-4">
            <BidJourneyTimeline journey={journey} bids={bids} />
          </td>
        </tr>
      )}

      {/* Modal de detalle */}
      {showDetailModal && buyerSummary && createPortal(
      <BuyerDetailModal
          buyer={buyerSummary}
          propertyId={propertyId}
          onClose={() => setShowDetailModal(false)}
      />,
      document.body
      )}
    </>
  );
};

/**
 * Fila de usuario invitado que NO ha pujado (expandible)
 */
export const NonBidderRow = ({
  buyer,
  journey,
  propertyId,
  isExpanded,
  onToggleExpand,
  isSpectatorMode = false
}) => {
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Enmascarar email si es modo espectador
  const displayEmail = isSpectatorMode ? maskEmail(buyer.email) : buyer.email;

  // Contar intentos
  const attemptCount = journey ? journey.length : 0;
  const hasActivity = attemptCount > 0;

  return (
    <>
      <tr 
        className={`border-b border-slate-700/50 ${hasActivity ? 'hover:bg-slate-800/30 cursor-pointer' : 'opacity-50'} transition-opacity`}
        onClick={hasActivity ? onToggleExpand : undefined}
      >
        {/* Expandir */}
        <td className="py-3 px-2 w-8">
          {hasActivity ? (
            <button className="p-1 hover:bg-slate-700 rounded transition-colors">
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </button>
          ) : (
            <span className="w-4 h-4" />
          )}
        </td>

        {/* Ranking (vacío) */}
        <td className="py-3 px-3">
          <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
            <span className="text-slate-500 text-xs">—</span>
          </div>
        </td>

        {/* Usuario */}
        <td className="py-3 px-3">
          <div>
            <p className="font-medium text-slate-400">{buyer.name || 'Sin nombre'}</p>
            <p className="text-xs text-slate-500">{displayEmail}</p>
          </div>
        </td>

        {/* Puja máxima (vacío) */}
        <td className="py-3 px-3">
          <span className="text-slate-600">—</span>
        </td>

        {/* Intentos */}
        <td className="py-3 px-3 text-center">
          {attemptCount > 0 ? (
            <span className="bg-orange-500/20 text-orange-400 text-xs px-2 py-1 rounded-full">
              {attemptCount}
            </span>
          ) : (
            <span className="text-slate-600">—</span>
          )}
        </td>

        {/* Resto de columnas vacías */}
        <td colSpan={4} className="py-3 px-3 text-center">
          <span className="text-slate-500 text-sm italic">
            {hasActivity ? 'Sin puja completada' : 'Sin actividad de puja'}
          </span>
        </td>

        {/* # Pujas */}
        <td className="py-3 px-3 text-center">
          <span className="text-slate-600">0</span>
        </td>

        {/* Ver detalle */}
        <td className="py-3 px-3" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => setShowDetailModal(true)}
            className="text-amber-400 hover:text-amber-300 transition-colors"
            title="Ver detalle completo"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </td>
      </tr>

      {/* Fila expandida con journey */}
      {isExpanded && hasActivity && (
        <tr className="bg-slate-900/50">
          <td colSpan={11} className="px-4 py-4">
            <BidJourneyTimeline journey={journey} bids={[]} />
          </td>
        </tr>
      )}

      {/* Modal de detalle */}
      {showDetailModal && createPortal(
        <BuyerDetailModal
          buyer={buyer}
          propertyId={propertyId}
          onClose={() => setShowDetailModal(false)}
        />,
        document.body
      )}
    </>
  );
};

export default {
  BidderRow,
  NonBidderRow,
  RankBadge,
  ConditionsTooltip,
  formatAmount,
  formatBidTime,
};